import anime from 'animejs';
import cx from 'classnames';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { updateEmojiClickedInVideoPlayer } from 'actions/videoPlayer';
import EMOJI_REACTIONS_MAP from 'constants/emojiReactions';
import { activateReaction, deleteReaction } from 'creators/videoPlayer';

import Emoji from 'components/ui-components/emoji';

import './styles.less';

const ANIMATION_VARIATION_COUNT = 4;

const propTypes = {
  type: PropTypes.oneOf(Object.values(EMOJI_REACTIONS_MAP)).isRequired,
  forceShow: PropTypes.bool.isRequired,
};
const EMOJI_WIDTH = 24 + 2 + 2;
const EMOJI_WIDTH_HALF = EMOJI_WIDTH / 2;

const MAX_ANON_USERNAME_LEN = 18;

class TimelineEmoji extends Component {
  videoPlayerWidth = 0;

  render() {
    const {
      type,
      username,
      showDelete,
      time,
      duration,
      iconClass,
    } = this.props;

    this._updateOffset();

    let reactionPositionPercentage = Math.round((time / duration) * 100);

    // do not let emojis or their tooltips get cutoff on edges of player
    let calculatedHalfWidth = EMOJI_WIDTH_HALF;
    const adjustedTooltipStyle = {};

    if (reactionPositionPercentage < 1) {
      reactionPositionPercentage = 0;
      calculatedHalfWidth = 0;
      adjustedTooltipStyle.left = '8px';
      adjustedTooltipStyle.transform = 'none';
    } else if (reactionPositionPercentage >= 99) {
      reactionPositionPercentage = 100;
      calculatedHalfWidth = 32;
    } else if (reactionPositionPercentage >= 95) {
      adjustedTooltipStyle.left = '-50%';
    }

    const currentReactionStyle = {
      left: `calc(${reactionPositionPercentage}% - ${calculatedHalfWidth}px)`,
    };

    return (
      <div
        ref={ref => (this.container = ref)}
        className={cx('video-player-timeline-emoji', 'reaction', iconClass)}
        style={currentReactionStyle}
      >
        <Emoji
          type={type}
          onClick={this.onClick}
          onMouseDown={this.onMouseDown}
        >
          <div
            onMouseDown={this._stopPropagation} // Block seeker from getting click
            onClick={showDelete ? this.onClickDelete : undefined}
            className={`video-player-timeline-emoji-username ${
              showDelete ? 'can-delete' : ''
            }`}
            style={adjustedTooltipStyle}
          >
            {username.length > MAX_ANON_USERNAME_LEN
              ? `${username.substr(0, MAX_ANON_USERNAME_LEN)}…`
              : username}
          </div>
        </Emoji>
        <div className="confettis">
          <span className="confetti-wrap">
            <span className="confetti"></span>
          </span>
          <span className="confetti-wrap">
            <span className="confetti"></span>
          </span>
          <span className="confetti-wrap">
            <span className="confetti"></span>
          </span>
          <span className="confetti-wrap">
            <span className="confetti"></span>
          </span>
        </div>
      </div>
    );
  }

  componentDidMount() {
    const { playSelectionAnimation, forceShow } = this.props;

    if (playSelectionAnimation) {
      this._playPopAnimation();
    } else if (!forceShow) {
      this._playPopAnimation();
    } else {
      this._playBounceAnimation();
    }

    if (this.inputElement) {
      this.inputElement.focus();
    }

    this.videoPlayerWidth = document.querySelector(
      '.video-player-container'
    ).offsetWidth;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // Control bar is being shown
    if (!this.props.forceShow && nextProps.forceShow) {
      this._playAnimation('forceFadeIn');
    }

    // Control bar is being hidden
    if (this.props.forceShow && !nextProps.forceShow) {
      this._playAnimation('forceFadeOut');
    }

    if (
      !this.props.playSelectionAnimation &&
      nextProps.playSelectionAnimation
    ) {
      this._playSelectionAnimation();
    }
  }

  onMouseDown = event => {
    // Prevent scrubber from start dragging
    event.stopPropagation();
    event.preventDefault();
  };

  onClick = ({ event }) => {
    const { id, type, time } = this.props;

    event.stopPropagation();
    event.preventDefault();

    this.props.updateEmojiClickedInVideoPlayer(true);
    this.props.onClick({ id, type, time });
  };

  onClickDelete = event => {
    const { id, type, time } = this.props;

    event.stopPropagation();
    event.preventDefault();

    this.props.onClickDelete({ id, type, time });
  };

  _stopPropagation(event) {
    event.stopPropagation();
  }

  _updateOffset() {
    const { time, duration, containerDimensions } = this.props;

    this._offset =
      (time / duration) * containerDimensions.width - EMOJI_WIDTH_HALF;
  }

  _playBounceAnimation() {
    const rise = this.props.rise || 0;

    // ** This is animation code **
    // There's no logic to these constants
    // beyond saying "that's what's in the design"
    this.bounceUp = anime({
      targets: this.container?.childNodes[0],
      translateY: {
        value: -5 - rise,
        duration: 800,
        elasticity: 600,
      },
      scale: {
        value: [0.89, 1],
        duration: 300,
        easing: [0, 2.06, 0.62, 2.1],
      },
    });

    const AFTER_FADEOUT_TIMEOUT = 1000;
    // in ms

    const SPEED_MULTIPLIER = 1 / anime.speed; // Match speed of others. Default is `1`

    // TODO: Can we make this a promise and pause the animation instead?
    //       it would eliminate the need for timers
    this.delayedFadeout = window.setTimeout(() => {
      // controls collapsed => mount => bounceUp in progress
      // => controls expand => avoid delayed fadeout while controls are expanded
      if (this.props.forceShow) {
        return;
      }

      this._playAnimation('bounceFadeOut');
    }, SPEED_MULTIPLIER * (this.bounceUp.duration + AFTER_FADEOUT_TIMEOUT));
  }

  _playAnimation(animation) {
    // Prevent delayed fadeout from triggering
    window.clearTimeout(this.delayedFadeout);

    // Avoid multiple animations from rapidly hiding/showing control bar
    if (this.currentAnimation) {
      this.currentAnimation.pause();
    }

    const common = {
      targets: this.container?.childNodes[0],
      easing: 'linear',
    };

    if (animation == 'bounceFadeOut') {
      this.currentAnimation = anime({
        ...common,
        duration: 400,
        opacity: 0,
      });
    } else if (animation == 'forceFadeIn') {
      this.currentAnimation = anime({
        ...common,
        duration: 200,
        opacity: 1,
        translateY: -5, // pop emoji back down to timeline if lifted
        scale: '+=0', // keep prev value
      });
    } else if (animation == 'forceFadeOut') {
      this.currentAnimation = anime({
        ...common,
        duration: 200,
        opacity: 0,
        translateY: -5, // pop emoji back down to timeline if lifted
        scale: '+=0', // keep prev value
      });
    }
  }

  _playSelectionAnimation() {
    const rise = this.props.rise || 0;
    const progress = this.props.time / this.props.duration;
    const endX =
      this.props.containerDimensions.width * progress -
      this._offset -
      EMOJI_WIDTH / 2;
    const selectionAnimationStartX = !isNaN(progress)
      ? progress * this.props.containerDimensions.width
      : 0;

    let startX =
      selectionAnimationStartX -
      this._offset -
      this.props.containerDimensions.x;

    // animate emojis as if they are coming from the center of the toolbar
    if (selectionAnimationStartX > this.props.containerDimensions.width / 2) {
      startX -= EMOJI_WIDTH;
    }

    const emojiRef = this.container?.childNodes[0];

    anime
      .timeline()
      .add({
        targets: this.container,
        duration: 1,
        zIndex: 10,
      })
      .add({
        targets: emojiRef,
        duration: 300,
        easing: 'easeOutQuad',
        translateX: [startX, endX],
        translateY: [-19 - rise, -19 - rise], // lift emoji off timeline if above toolbar
        rotate: [0, startX < endX ? 20 : -20],
        scale: [1.34],
      })
      .add({
        targets: emojiRef,
        duration: 288,
        easing: 'easeOutQuad',
        translateX: '+=0', // keep prev value
        translateY: -5 - rise, // lift emoji off timeline if above toolbar
        scale: [1.34, 1],
        rotate: 0,
      })
      .add({
        targets: this.container,
        duration: 1,
        zIndex: 0,
      });
  }

  _playPopAnimation() {
    const emojiRef = this.container;

    const animationVersion = `pop-animation-variation-${Math.floor(
      Math.random() * ANIMATION_VARIATION_COUNT + 1
    )}`;

    emojiRef.classList.add('bounce', animationVersion);

    emojiRef.addEventListener('animationend', ({ animationName }) => {
      if (animationName === 'bounce') {
        emojiRef.classList.remove('bounce', animationVersion);

        // If the control bar is open don't fade out
        if (!this.props.forceShow) {
          this._playAnimation('bounceFadeOut');
        }
      }
    });
  }
}

TimelineEmoji.propTypes = propTypes;

const mapStateToProps = () => {
  return {};
};

const mapDispatchToProps = dispatch => ({
  onClickDelete: id => dispatch(deleteReaction(id)),
  onClick: reaction => dispatch(activateReaction(reaction)),
  updateEmojiClickedInVideoPlayer: clicked =>
    dispatch(updateEmojiClickedInVideoPlayer(clicked)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TimelineEmoji);
